import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, Plus, Edit2, Trash2, Briefcase, Receipt, Paperclip, X as XIcon, FileText } from "lucide-react";
import { api } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/lib/toast";
import { BidStatus, Empty, PageHeader, SlideOver, Spinner, Field } from "@/components/UI";
import { formatCurrency, formatDate } from "@/lib/format";

const STATUSES = ["Draft", "Bidding", "Sent", "Negotiating", "Won", "Lost", "Cancelled", "Expired"];
const SERVICE_TYPES = [
  "ICT Hardware", "Networking", "Cybersecurity", "Cloud / SaaS",
  "Software", "Infrastructure", "CCTV / Surveillance", "Consulting", "Other",
];
const CURRENCIES = ["UGX", "USD", "CAD", "EUR", "GBP", "KES"];

const EMPTY_BID = {
  title: "", client_name: "", status: "Draft", service_type: "",
  bid_amount: 0, currency: "UGX",
  bid_date: new Date().toISOString().slice(0, 10),
  deadline: "", scope_of_work: "", notes: "", ref_number: "",
  assignee_id: null,
  attachments: [],
};

export default function Bids() {
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [editor, setEditor] = useState({ open: false, bid: null });
  const [team, setTeam] = useState([]);
  const { toast } = useToast();
  const [params, setParams] = useSearchParams();
  const navTo = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setBids(await api.listBids());
    } catch {
      toast("Failed to load bids", "error");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
    api.team().then(setTeam).catch(() => {});
  }, [load]);

  useEffect(() => {
    if (params.get("new") === "1") {
      setEditor({ open: true, bid: null });
      params.delete("new");
      setParams(params, { replace: true });
    }
  }, [params, setParams]);

  const save = async (form) => {
    try {
      if (editor.bid?.id) {
        await api.updateBid(editor.bid.id, form);
        toast("Bid updated", "success");
      } else {
        await api.createBid(form);
        toast("Bid created", "success");
      }
      setEditor({ open: false, bid: null });
      load();
    } catch (e) {
      toast(e?.response?.data?.detail || "Save failed", "error");
    }
  };

  const remove = async (b) => {
    if (!window.confirm(`Delete "${b.title}"?`)) return;
    try {
      await api.deleteBid(b.id);
      toast("Bid deleted", "success");
      load();
    } catch {
      toast("Delete failed", "error");
    }
  };

  const filtered = bids.filter((b) => {
    if (statusFilter !== "all" && b.status !== statusFilter) return false;
    if (!search) return true;
    const s = search.toLowerCase();
    return ["title", "client_name", "ref_number"]
      .some((k) => (b[k] || "").toLowerCase().includes(s));
  });

  return (
    <div data-testid="page-bids" className="space-y-5">
      <PageHeader
        icon={Briefcase}
        title="My Bids"
        subtitle={`${bids.length} total · ${filtered.length} shown`}
        action={
          <button
            data-testid="new-bid"
            onClick={() => setEditor({ open: true, bid: null })}
            className="btn btn-primary"
          >
            <Plus size={14} /> New Bid
          </button>
        }
      />

      <div className="card card-tight flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[220px] relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="search"
            placeholder="Search title, client, reference…"
            className="input pl-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select
          data-testid="status-filter"
          className="select max-w-[180px]"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="all">All statuses</option>
          {STATUSES.map((s) => <option key={s}>{s}</option>)}
        </select>
      </div>

      <div className="card overflow-hidden">
        {loading ? (
          <div className="empty"><Spinner /> &nbsp;Loading bids…</div>
        ) : filtered.length === 0 ? (
          <Empty
            icon={Briefcase}
            title="No bids match"
            description="Try clearing your filters or create a new bid."
            action={
              <button onClick={() => setEditor({ open: true, bid: null })} className="btn btn-primary" data-testid="empty-new">
                <Plus size={14} /> Create your first bid
              </button>
            }
          />
        ) : (
          <>
            {/* Mobile cards (< lg) */}
            <div className="lg:hidden divide-y divide-slate-100">
              {filtered.map((b) => {
                const lead = (team || []).find((m) => m.id === b.assignee_id);
                return (
                  <div
                    key={b.id}
                    data-testid={`row-${b.id}`}
                    className="p-4 space-y-2"
                    onClick={() => setEditor({ open: true, bid: b })}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setEditor({ open: true, bid: b }); }}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold text-sm break-words">{b.title}</div>
                        <div className="text-xs muted mt-0.5 break-words">{b.client_name || "—"}</div>
                      </div>
                      <BidStatus status={b.status} />
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-slate-500">{b.ref_number || "—"} · {b.service_type || "—"}</span>
                      <span className="font-semibold">{formatCurrency(b.bid_amount, b.currency)}</span>
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="muted">Due {formatDate(b.deadline) || "—"}</span>
                      {lead && (
                        <span
                          data-testid={`lead-${b.id}`}
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold"
                          style={{ background: "var(--green-50)", color: "var(--green-700)" }}
                        >
                          <span
                            className="w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-bold text-white"
                            style={{ background: "var(--green-700)" }}
                          >
                            {(lead.full_name || lead.email).slice(0, 1).toUpperCase()}
                          </span>
                          {(lead.full_name || lead.email).split(" ")[0]}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 pt-1 border-t border-slate-100" onClick={(e) => e.stopPropagation()}>
                      <button
                        data-testid={`tobid-quote-${b.id}-mobile`}
                        onClick={() => navTo(`/dms/quotations?from_bid=${b.id}`)}
                        className="text-xs text-slate-600 flex items-center gap-1"
                      >
                        <Receipt size={13} /> Quote
                      </button>
                      <button
                        data-testid={`edit-bid-${b.id}-mobile`}
                        onClick={() => setEditor({ open: true, bid: b })}
                        className="text-xs text-slate-600 flex items-center gap-1"
                      >
                        <Edit2 size={13} /> Edit
                      </button>
                      <button
                        data-testid={`del-bid-${b.id}-mobile`}
                        onClick={() => remove(b)}
                        className="text-xs text-red-600 flex items-center gap-1 ml-auto"
                      >
                        <Trash2 size={13} /> Delete
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Desktop table (>= lg) */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="tbl">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Client</th>
                  <th>Status</th>
                  <th style={{ textAlign: "right" }}>Amount</th>
                  <th>Deadline</th>
                  <th style={{ textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((b) => {
                  const lead = (team || []).find((m) => m.id === b.assignee_id);
                  return (
                  <tr key={b.id} data-testid={`row-desktop-${b.id}`}>
                    <td>
                      <div className="font-semibold">{b.title}</div>
                      <div className="text-xs muted flex items-center gap-2 flex-wrap">
                        <span>{b.ref_number || "—"} · {b.service_type || "—"}</span>
                        {lead && (
                          <span
                            data-testid={`lead-desktop-${b.id}`}
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold"
                            style={{ background: "var(--green-50)", color: "var(--green-700)" }}
                            title={`Lead: ${lead.full_name || lead.email}`}
                          >
                            <span
                              className="w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-bold text-white"
                              style={{ background: "var(--green-700)" }}
                            >
                              {(lead.full_name || lead.email).slice(0, 1).toUpperCase()}
                            </span>
                            {(lead.full_name || lead.email).split(" ")[0]}
                          </span>
                        )}
                      </div>
                    </td>
                    <td>{b.client_name || "—"}</td>
                    <td><BidStatus status={b.status} /></td>
                    <td style={{ textAlign: "right", fontWeight: 600 }}>
                      {formatCurrency(b.bid_amount, b.currency)}
                    </td>
                    <td>{formatDate(b.deadline)}</td>
                    <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                      <button
                        data-testid={`tobid-quote-${b.id}`}
                        onClick={() => navTo(`/dms/quotations?from_bid=${b.id}`)}
                        className="text-slate-500 hover:text-[#0e6939] mr-3"
                        title="Create quotation from this bid"
                      >
                        <Receipt size={14} />
                      </button>
                      <button
                        data-testid={`edit-bid-${b.id}`}
                        onClick={() => setEditor({ open: true, bid: b })}
                        className="text-slate-500 hover:text-[#0e6939] mr-3"
                      >
                        <Edit2 size={14} />
                      </button>
                      <button
                        data-testid={`del-bid-${b.id}`}
                        onClick={() => remove(b)}
                        className="text-slate-500 hover:text-red-600"
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      <BidEditor
        open={editor.open}
        bid={editor.bid}
        team={team}
        onClose={() => setEditor({ open: false, bid: null })}
        onSave={save}
      />
    </div>
  );
}

function BidEditor({ open, bid, team, onClose, onSave }) {
  const [form, setForm] = useState(EMPTY_BID);
  useEffect(() => setForm(bid || EMPTY_BID), [bid, open]);

  return (
    <SlideOver
      open={open}
      onClose={onClose}
      title={bid?.id ? "Edit bid" : "New bid"}
      subtitle={bid?.ref_number || ""}
      footer={
        <>
          <button data-testid="cancel-bid" onClick={onClose} className="btn btn-ghost">Cancel</button>
          <button
            data-testid="save-bid"
            onClick={() => onSave(form)}
            className="btn btn-primary"
          >
            {bid?.id ? "Save changes" : "Create bid"}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Title *">
          <input
            data-testid="bid-title"
            required
            className="input"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
          />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Client">
            <input
              data-testid="bid-client"
              className="input"
              value={form.client_name}
              onChange={(e) => setForm({ ...form, client_name: e.target.value })}
            />
          </Field>
          <Field label="Reference">
            <input
              data-testid="bid-ref"
              className="input"
              value={form.ref_number}
              onChange={(e) => setForm({ ...form, ref_number: e.target.value })}
            />
          </Field>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <Field label="Status">
            <select
              data-testid="bid-status"
              className="select"
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value })}
            >
              {STATUSES.map((s) => <option key={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Service type">
            <select
              className="select"
              value={form.service_type}
              onChange={(e) => setForm({ ...form, service_type: e.target.value })}
            >
              <option value="">—</option>
              {SERVICE_TYPES.map((s) => <option key={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Currency">
            <select
              data-testid="bid-currency"
              className="select"
              value={form.currency}
              onChange={(e) => setForm({ ...form, currency: e.target.value })}
            >
              {CURRENCIES.map((c) => <option key={c}>{c}</option>)}
            </select>
          </Field>
        </div>

        <Field label="Lead (assignee)">
          <select
            data-testid="bid-assignee"
            className="select"
            value={form.assignee_id || ""}
            onChange={(e) => setForm({ ...form, assignee_id: e.target.value || null })}
          >
            <option value="">— Unassigned —</option>
            {(team || [])
              .filter((m) => m.role !== "customer" && m.is_active)
              .map((m) => (
                <option key={m.id} value={m.id}>
                  {m.full_name || m.email} · {m.role}
                </option>
              ))}
          </select>
          <div className="text-[11px] muted mt-1">
            The assigned team member is emailed and gets an in-app notification when set or changed.
          </div>
        </Field>

        <div className="grid grid-cols-3 gap-3">
          <Field label="Amount">
            <input
              data-testid="bid-amount"
              type="number"
              step="0.01"
              className="input"
              value={form.bid_amount}
              onChange={(e) => setForm({ ...form, bid_amount: parseFloat(e.target.value) || 0 })}
            />
          </Field>
          <Field label="Bid date">
            <input
              type="date"
              className="input"
              value={form.bid_date || ""}
              onChange={(e) => setForm({ ...form, bid_date: e.target.value })}
            />
          </Field>
          <Field label="Deadline">
            <input
              data-testid="bid-deadline"
              type="date"
              className="input"
              value={form.deadline || ""}
              onChange={(e) => setForm({ ...form, deadline: e.target.value })}
            />
          </Field>
        </div>

        <Field label="Scope of work">
          <textarea
            data-testid="bid-scope"
            rows={4}
            className="textarea"
            value={form.scope_of_work}
            onChange={(e) => setForm({ ...form, scope_of_work: e.target.value })}
          />
        </Field>

        <Field label="Notes">
          <textarea
            rows={2}
            className="textarea"
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
          />
        </Field>

        <BidAttachments
          attachments={form.attachments || []}
          onChange={(next) => setForm({ ...form, attachments: next })}
        />
      </div>
    </SlideOver>
  );
}

function BidAttachments({ attachments, onChange }) {
  const [vault, setVault] = useState([]);
  const [loading, setLoading] = useState(true);
  const [picking, setPicking] = useState(false);

  useEffect(() => {
    api.listDocuments()
      .then(setVault)
      .catch(() => setVault([]))
      .finally(() => setLoading(false));
  }, []);

  const attached = vault.filter((d) => attachments.includes(d.id));
  const available = vault.filter((d) => !attachments.includes(d.id));

  const remove = (id) => onChange(attachments.filter((x) => x !== id));
  const add = (id) => {
    onChange([...attachments, id]);
    setPicking(false);
  };

  return (
    <Field label="Vault attachments">
      <div className="space-y-2">
        {attached.length === 0 && !picking && (
          <div className="text-xs muted">No attachments. Link compliance certificates, signed quotations, or supporting docs.</div>
        )}
        {attached.map((d) => (
          <div
            key={d.id}
            data-testid={`attached-${d.id}`}
            className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg"
            style={{ background: "var(--green-50)", border: "1px solid #d6e8de" }}
          >
            <div className="flex items-center gap-2 min-w-0">
              <FileText size={14} style={{ color: "var(--green-700)" }} className="shrink-0" />
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">{d.file_name}</div>
                <div className="text-xs muted">
                  {d.category || d.folder}
                  {d.expiry_date ? ` · expires ${d.expiry_date}` : ""}
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => remove(d.id)}
              className="text-slate-400 hover:text-red-600 shrink-0"
              title="Remove attachment"
              data-testid={`detach-${d.id}`}
            >
              <XIcon size={14} />
            </button>
          </div>
        ))}

        {picking ? (
          <div
            className="rounded-lg border border-slate-200 max-h-60 overflow-y-auto"
            data-testid="attach-picker"
          >
            {loading ? (
              <div className="p-3 text-xs muted">Loading vault…</div>
            ) : available.length === 0 ? (
              <div className="p-3 text-xs muted">No more documents to attach. Upload to the Vault first.</div>
            ) : (
              available.map((d) => (
                <button
                  type="button"
                  key={d.id}
                  data-testid={`attach-pick-${d.id}`}
                  onClick={() => add(d.id)}
                  className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-100 last:border-0"
                >
                  <div className="text-sm font-medium">{d.file_name}</div>
                  <div className="text-xs muted">
                    {d.category || d.folder}
                    {d.expiry_date ? ` · expires ${d.expiry_date}` : ""}
                  </div>
                </button>
              ))
            )}
          </div>
        ) : (
          <button
            type="button"
            data-testid="attach-from-vault"
            onClick={() => setPicking(true)}
            className="btn btn-ghost text-xs"
          >
            <Paperclip size={12} /> Attach from Vault
          </button>
        )}
      </div>
    </Field>
  );
}
